import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authService, type UserProfile } from '../services/auth.service';
import { isAuthenticated, clearTokens } from '../services/api';
import i18n from '../i18n';

interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<UserProfile>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Clears staff user from context without calling the API (e.g. after handoff to portal JWT). */
  clearStaffUser: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated()) {
      setState({ user: null, loading: false, error: null });
      return;
    }
    try {
      const user = await authService.me();
      setState({ user, loading: false, error: null });
    } catch {
      clearTokens();
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string): Promise<UserProfile> => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await authService.login({ email, password });
      const user = await authService.me();
      setState({ user, loading: false, error: null });
      return user;
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || i18n.t('login.errorInvalidCredentials');
      setState({ user: null, loading: false, error: message });
      throw err;
    }
  };

  const clearStaffUser = useCallback(() => {
    setState({ user: null, loading: false, error: null });
  }, []);

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setState({ user: null, loading: false, error: null });
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser, clearStaffUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
